import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import type { Billing } from "@/api.types";
import { AutocompleteAddress } from "@/components/address-auto-complete";
import { logger } from "@/services/logger";
import { MapPin, Pencil, Plus } from "lucide-react";

type BillingFormValues = {
  line1: string;
  line2?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  location: {
    lat: number;
    lng: number;
  };
  plusCode?: string;
};

export default function BillingScreen() {
  // Opt out of React Compiler auto-memoization (incompatible with
  // react-hook-form's form.watch() proxy).
  "use no memo";
  const { t } = useTranslation("app");
  const { token, customer } = useSession();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const billingSchema = z.object({
    line1: z.string().min(1, t("validation.addressLine1Required")),
    line2: z.string().optional(),
    city: z.string().min(1, t("validation.cityRequired")),
    province: z.string().min(1, t("validation.provinceRequired")),
    country: z.string().min(1, t("validation.countryRequired")),
    postalCode: z.string().min(1, t("validation.postalCodeRequired")),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    plusCode: z.string().optional(),
  });

  const billingAddress = customer?.billing?.address;
  const hasBillingAddress = !!billingAddress?.line1;

  const form = useForm<BillingFormValues>({
    resolver: zodResolver(billingSchema),
    defaultValues: {
      line1: "",
      line2: "",
      city: "",
      province: "",
      country: "",
      postalCode: "",
      location: { lat: 0, lng: 0 },
    },
    values: billingAddress
      ? {
          line1: billingAddress.line1,
          line2: billingAddress.line2 ?? "",
          city: billingAddress.city,
          province: billingAddress.province,
          country: billingAddress.country,
          postalCode: billingAddress.postalCode ?? "",
          location: billingAddress.location,
        }
      : undefined,
  });

  const createBillingMutation = useMutation({
    mutationFn: async (values: BillingFormValues) => {
      const { data } = await axios.post<Billing>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/billing`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer", "me"],
      });
      setDialogOpen(false);
    },
  });

  const updateBillingMutation = useMutation({
    mutationFn: async (values: BillingFormValues) => {
      const { data } = await axios.patch<Billing>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/billing`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer", "me"],
      });
      setDialogOpen(false);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (customer?.billing) {
      updateBillingMutation.mutate(values, {
        onError: (error) => {
          logger.error("Failed to update billing:", error);
        },
      });
    } else {
      createBillingMutation.mutate(values, {
        onError: (error) => {
          logger.error("Failed to create billing:", error);
        },
      });
    }
  });

  const formatAddress = () => {
    if (!billingAddress) return null;
    const parts = [
      billingAddress.line1,
      billingAddress.line2,
      [billingAddress.city, billingAddress.province, billingAddress.postalCode]
        .filter(Boolean)
        .join(", "),
      billingAddress.country,
    ].filter(Boolean);
    return parts;
  };

  const isSaving =
    createBillingMutation.isPending || updateBillingMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t("billing.title")}</h1>
        <p className="text-default-500 mt-1">{t("billing.lede")}</p>
      </div>

      <Card shadow="sm">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-primary/15 rounded-lg shrink-0">
              <MapPin className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold">
                {t("billing.addressTitle")}
              </h3>
              <p className="text-sm text-default-500">
                {t("billing.addressDescription")}
              </p>
            </div>
          </div>
          <Button
            variant="flat"
            size="sm"
            className="w-full sm:w-auto"
            onPress={() => setDialogOpen(true)}
            startContent={
              hasBillingAddress ? (
                <Pencil className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )
            }
          >
            {hasBillingAddress ? t("actions.edit") : t("billing.addAddress")}
          </Button>
        </CardHeader>
        <CardBody>
          {hasBillingAddress ? (
            <div className="text-sm space-y-1">
              {formatAddress()?.map((line, index) => (
                <p
                  key={index}
                  className={index === 0 ? "font-medium" : "text-default-500"}
                >
                  {line}
                </p>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 bg-default-100 rounded-full flex items-center justify-center mb-3">
                <MapPin className="h-6 w-6 text-default-400" />
              </div>
              <p className="text-default-500">{t("billing.noAddress")}</p>
              <p className="text-sm text-default-400 mt-1">
                {t("billing.noAddressHint")}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <form onSubmit={onSubmit}>
              <ModalHeader className="flex-col items-start gap-1">
                <span>
                  {hasBillingAddress
                    ? t("billing.editTitle")
                    : t("billing.addTitle")}
                </span>
                <span className="text-small text-default-500 font-normal">
                  {t("billing.modalSubtitle")}
                </span>
              </ModalHeader>
              <ModalBody className="gap-4">
                <AutocompleteAddress
                  onSelectedAddress={(selectedAddress) => {
                    if (selectedAddress) {
                      form.setValue(
                        "line1",
                        selectedAddress.addressComponents.line1
                      );
                      form.setValue(
                        "line2",
                        selectedAddress.addressComponents.line2 || ""
                      );
                      form.setValue(
                        "city",
                        selectedAddress.addressComponents.city
                      );
                      form.setValue(
                        "province",
                        selectedAddress.addressComponents.state
                      );
                      form.setValue(
                        "country",
                        selectedAddress.addressComponents.country
                      );
                      form.setValue(
                        "postalCode",
                        selectedAddress.addressComponents.postalCode || ""
                      );
                      form.setValue("location", selectedAddress.location);
                    }
                  }}
                  placeholder={t("billing.searchPlaceholder")}
                />

                {form.watch("line1") && (
                  <div className="p-3 bg-default-100 rounded-lg text-sm">
                    <p className="font-medium">
                      {t("billing.selectedAddress")}
                    </p>
                    <p>{form.watch("line1")}</p>
                    {form.watch("line2") && <p>{form.watch("line2")}</p>}
                    <p>
                      {[
                        form.watch("city"),
                        form.watch("province"),
                        form.watch("postalCode"),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    <p>{form.watch("country")}</p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="light" type="button" onPress={onClose}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSaving || !form.watch("line1")}
                  isLoading={isSaving}
                >
                  {isSaving ? t("actions.saving") : t("actions.save")}
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
